import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { BatchPassport } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { batchStatusLabel, formatHrDate, formatHrDateTime, formatNum } from "./batchUtils";
import BatchMetricsChart from "./BatchMetricsChart";

type TabType = "pregled" | "parametri" | "metrike" | "sirovine" | "gotovo" | "osoblje" | "povijest";

const TABS: { id: TabType; label: string }[] = [
  { id: "pregled", label: "Pregled" },
  { id: "parametri", label: "Procesni parametri" },
  { id: "metrike", label: "Metrike" },
  { id: "sirovine", label: "Sirovine (lotovi)" },
  { id: "gotovo", label: "Gotovi proizvodi" },
  { id: "osoblje", label: "Osoblje" },
  { id: "povijest", label: "Povijest statusa" },
];

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="detail-item">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value ?? "-"}</span>
  </div>
);

const BatchDetail = () => {
  savePathToLocalStorage();
  const history = useHistory();
  const { batchId: batchIdParam } = useParams<{ batchId: string }>();
  const batchId = Number(batchIdParam);

  const [batch, setBatch] = useState<BatchPassport | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("pregled");

  useEffect(() => {
    if (!batchId || Number.isNaN(batchId)) return;
    (async () => {
      setLoading(true);
      try {
        const data = await mesApi.getBatch(batchId);
        setBatch(data);
      } catch (err: any) {
        toast.error("Greška pri učitavanju batcha: " + err.message);
        setBatch(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [batchId]);

  const lots = batch?.BatchLotConsumption || [];
  const staff = batch?.BatchStaff || [];
  const statusLog = batch?.BatchStatusLog || [];
  const finished = batch?.FinishedGoodsStock || [];
  const params = batch?.ProcessParameter || [];
  const timeline = batch?.Timeline_TimelineToTimelineBatch;

  const renderPregled = () => (
    <>
      <div className="trial-info-grid">
        <Detail label="Kod (MixCode)" value={batch?.MixCode} />
        <Detail label="Lot proizvoda" value={batch?.ProductionLotCode || "—"} />
        <Detail label="Proizvod" value={batch?.ProductName} />
        <Detail label="Recept" value={batch?.RecipeCode} />
        <Detail label="Datum proizvodnje" value={formatHrDate(timeline?.ProductionDate)} />
        <Detail label="Redoslijed" value={batch?.Sequence} />
        <Detail label="Verzija timelinea" value={timeline?.Version} />
        <Detail label="Mikser" value={batch?.MixerName} />
        <Detail label="Procesna linija" value={batch?.ProcessName} />
        <Detail label="Peć" value={batch?.OvenName} />
        <Detail label="Finalna masa (kg)" value={formatNum(batch?.FinalMassKg)} />
        <Detail
          label="Status"
          value={<span className={`status-pill status-${batch?.BatchStatus}`}>{batchStatusLabel(batch?.BatchStatus)}</span>}
        />
      </div>

      <h4 style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}>Vremena procesa</h4>
      <div className="mes-table-wrap">
        <table className="mes-table">
        <thead>
          <tr>
            <th>Faza</th>
            <th>Početak</th>
            <th>Kraj</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Miješanje</td>
            <td>{formatHrDateTime(batch?.MixingStart)}</td>
            <td>{formatHrDateTime(batch?.MixingEnd)}</td>
          </tr>
          <tr>
            <td>Fermentacija</td>
            <td>{formatHrDateTime(batch?.FermentationStart)}</td>
            <td>{formatHrDateTime(batch?.FermentationEnd)}</td>
          </tr>
          <tr>
            <td>Obrada</td>
            <td>{formatHrDateTime(batch?.ProcessingStart)}</td>
            <td>{formatHrDateTime(batch?.ProcessingEnd)}</td>
          </tr>
          <tr>
            <td>Završno dizanje</td>
            <td>{formatHrDateTime(batch?.FinalProofStart)}</td>
            <td>{formatHrDateTime(batch?.FinalProofEnd)}</td>
          </tr>
          <tr>
            <td>Pečenje</td>
            <td>{formatHrDateTime(batch?.BakingStart)}</td>
            <td>{formatHrDateTime(batch?.BakingEnd)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </>
  );

  const renderParametri = () => {
    if (params.length === 0) {
      return <p className="empty-state">Nema zabilježenih procesnih parametara za ovaj batch.</p>;
    }
    return (
      <>
        {params.map((p: any) => (
          <div key={p.ID} className="trial-info-grid" style={{ marginBottom: "1.5rem" }}>
            <Detail label="Masa tijesta (kg)" value={formatNum(p.DoughMassKg)} />
            <Detail label="Temp. tijesta (°C)" value={formatNum(p.DoughTempC)} />
            <Detail label="pH starter" value={formatNum(p.StarterPh, 3)} />
            <Detail label="pH ferm. 1 / 2" value={`${formatNum(p.BulkFermentationPh1, 3)} / ${formatNum(p.BulkFermentationPh2, 3)}`} />
            <Detail label="Visina ferm. 1 / 2" value={`${formatNum(p.BulkFermentationHeight1)} / ${formatNum(p.BulkFermentationHeight2)}`} />
            <Detail label="Visina zavr. 1 / 2" value={`${formatNum(p.FinalProofHeight1)} / ${formatNum(p.FinalProofHeight2)}`} />
            <Detail label="Spec. volumen (L/kg)" value={formatNum(p.SpecificVolumeLkg, 3)} />
            <Detail label="Proizvedeno (kom)" value={p.ProducedPieces ?? "-"} />
            <Detail label="Pakirano (kom)" value={p.PackedPieces ?? "-"} />
            <Detail label="Škart (kom)" value={p.Scrap ?? "-"} />
            <Detail label="Škart (%)" value={formatNum(p.ScrapPct)} />
            <Detail label="Napomena" value={p.Notes || "-"} />
          </div>
        ))}
      </>
    );
  };

  const sourceLabel = (src?: string | null) => {
    switch (src) {
      case "auto_fifo":
        return "Auto (FIFO)";
      case "manual":
        return "Ručno";
      case null:
      case undefined:
      case "":
        return "-";
      default:
        return src;
    }
  };

  const renderSirovine = () => {
    if (lots.length === 0) {
      return <p className="empty-state">Nema evidencije utrošenih lotova sirovina za ovaj batch.</p>;
    }
    const totalPlanned = lots.reduce((s, r) => s + (Number(r.PlannedKg) || 0), 0);
    const totalActual = lots.reduce((s, r) => s + (Number(r.ActualKg) || 0), 0);
    return (
      <div className="mes-table-wrap">
        <table className="mes-table">
        <thead>
          <tr>
            <th>Stavka</th>
            <th>Vrsta</th>
            <th>Broj lota</th>
            <th>Dobavljač / izvor lota</th>
            <th style={{ textAlign: "right" }}>Plan (kg)</th>
            <th style={{ textAlign: "right" }}>Stvarno (kg)</th>
            <th>Dodjela</th>
          </tr>
        </thead>
        <tbody>
          {lots.map((row) => {
            const lot = row.MaterialLot_BatchLotConsumptionToMaterialLot;
            const isSemi = lot?.MaterialType === "semi_finished";
            const name =
              row.Ingredient_BatchLotConsumptionToIngredient?.IngredientName ||
              (isSemi ? lot?.Notes || "Polu-proizvod" : null) ||
              "-";
            const supplier = isSemi
              ? "Interna proizvodnja"
              : lot?.Supplier_MaterialLotToSupplier?.Name || "-";
            return (
              <tr key={row.ID}>
                <td>{name}</td>
                <td>
                  <span className={`type-pill ${isSemi ? "type-semi" : "type-raw"}`}>
                    {isSemi ? "Polu-proizvod" : "Sirovina"}
                  </span>
                </td>
                <td>
                  <strong>{(isSemi ? lot?.InternalLotCode : lot?.LotNumberSupplier || lot?.InternalLotCode) || "-"}</strong>
                </td>
                <td>{supplier}</td>
                <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatNum(row.PlannedKg, 3)}</td>
                <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatNum(row.ActualKg, 3)}</td>
                <td>{sourceLabel(row.Source)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
              Ukupno
            </td>
            <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{formatNum(totalPlanned, 3)}</td>
            <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{formatNum(totalActual, 3)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      </div>
    );
  };

  const renderGotovo = () => {
    if (finished.length === 0) {
      return <p className="empty-state">Nema evidentiranih gotovih proizvoda za ovaj batch.</p>;
    }
    return (
      <div className="mes-table-wrap">
        <table className="mes-table">
        <thead>
          <tr>
            <th>Proizvod</th>
            <th style={{ textAlign: "right" }}>Količina (kom)</th>
            <th>Datum</th>
          </tr>
        </thead>
        <tbody>
          {finished.map((row) => (
            <tr key={row.ID}>
              <td>{row.Product_FinishedGoodsStockToProduct?.ProductName || "-"}</td>
              <td style={{ textAlign: "right", fontFamily: "monospace" }}>{row.QuantityPieces}</td>
              <td>{formatHrDate(row.StockDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  const renderOsoblje = () => {
    if (staff.length === 0) {
      return <p className="empty-state">Nije dodijeljeno osoblje za ovaj batch.</p>;
    }
    return (
      <div className="mes-table-wrap">
        <table className="mes-table">
        <thead>
          <tr>
            <th>Djelatnik</th>
            <th>Pozicija</th>
            <th>Uloga</th>
          </tr>
        </thead>
        <tbody>
          {staff.map((row) => {
            const s = row.Staff_BatchStaffToStaff;
            const name = s?.FullName || [s?.FirstName, s?.LastName].filter(Boolean).join(" ") || "-";
            return (
              <tr key={row.ID}>
                <td>{name}</td>
                <td>{s?.Position || "-"}</td>
                <td>{row.Role || "-"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    );
  };

  const renderPovijest = () => {
    if (statusLog.length === 0) {
      return <p className="empty-state">Nema zabilježenih promjena statusa.</p>;
    }
    return (
      <div className="mes-table-wrap">
        <table className="mes-table">
        <thead>
          <tr>
            <th>Vrijeme</th>
            <th>Iz statusa</th>
            <th>U status</th>
            <th>Izvor</th>
          </tr>
        </thead>
        <tbody>
          {statusLog.map((row) => (
            <tr key={row.ID}>
              <td>{formatHrDateTime(row.ChangedAt)}</td>
              <td>{batchStatusLabel(row.OldStatus)}</td>
              <td>
                <span className={`status-pill status-${row.NewStatus}`}>{batchStatusLabel(row.NewStatus)}</span>
              </td>
              <td>{row.Source || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  return (
    <div className="mes-page trial-detail-page">
      <Header title="MES - Putovnica batcha" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2000} />

          {loading && <div className="mes-section loading-state">Učitavanje…</div>}

          {!loading && !batch && (
            <div className="mes-section empty-state">
              <p>Batch nije pronađen.</p>
              <button type="button" className="btn-secondary" onClick={() => history.push("/mes/batches")}>
                Natrag na listu
              </button>
            </div>
          )}

          {!loading && batch && (
            <>
              <header className="mes-section trial-detail-header">
                <button type="button" className="btn-secondary btn-sm" onClick={() => history.push("/mes/batches")}>
                  ← Batchevi
                </button>
                <div className="trial-detail-title">
                  <h2 style={{ margin: 0 }}>{batch.MixCode}</h2>
                  <p className="mes-hint" style={{ margin: "0.25rem 0 0" }}>
                    {batch.ProductionLotCode ? <>Lot: {batch.ProductionLotCode} · </> : null}
                    {batch.ProductName || "—"} · {formatHrDate(timeline?.ProductionDate)} ·{" "}
                    <span className={`status-pill status-${batch.BatchStatus}`}>{batchStatusLabel(batch.BatchStatus)}</span>
                  </p>
                </div>
              </header>

              <div className="mes-section">
                <div className="kpi-tabs fq-analysis-tabs">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`kpi-tab ${activeTab === t.id ? "active" : ""}`}
                      onClick={() => setActiveTab(t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mes-section trial-detail-panel">
                {activeTab === "pregled" && renderPregled()}
                {activeTab === "parametri" && renderParametri()}
                {activeTab === "metrike" && <BatchMetricsChart batchId={batchId} />}
                {activeTab === "sirovine" && renderSirovine()}
                {activeTab === "gotovo" && renderGotovo()}
                {activeTab === "osoblje" && renderOsoblje()}
                {activeTab === "povijest" && renderPovijest()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;
